import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { RegisterAccessModalComponent } from '@assetaccountant/organisation-dashboard/components/register-access-modal/register-access-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';

import { OrganisationListItem } from '@assetaccountant/api/models/organisation-list-item';
import { OrganisationPermissionType } from '@assetaccountant/api/models/organisation-permission-type';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { RegisterService } from '..';
import { AuthorizationService } from '../services/authorization.service';
import { LayoutService } from '../services/layout.service';
import { firstWithContext } from '../utils/first-with-context';
import { setupModal, TypedNgbModal } from '../utils/typed-modal';

export const CanActivateRegisterAccess: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const registerService = inject(RegisterService);
  const organisationApi = inject(OrganisationV2Api);
  const authorizationService = inject(AuthorizationService);
  const ngbModal = inject(NgbModal) as TypedNgbModal;
  const router = inject(Router);
  const layoutService = inject(LayoutService);

  return RegisterAccessGuard.canActivate(
    route,
    registerService,
    organisationApi,
    authorizationService,
    ngbModal,
    router,
    layoutService,
  );
};

export class RegisterAccessGuard {
  static async canActivate(
    route: ActivatedRouteSnapshot,
    registerService: RegisterService,
    organisationApi: OrganisationV2Api,
    authorizationService: AuthorizationService,
    ngbModal: TypedNgbModal,
    router: Router,
    layoutService: LayoutService,
  ) {
    const registerId = +route.params.registerId;
    const organisationId = +route.params.organisationId;

    // Allow wildcard ids: '-'
    if (isNaN(registerId)) {
      return true;
    }

    // Does the user have access to the register
    try {
      return !!(await firstValueFrom(registerService.getRegister(registerId)));
    } catch (error) {
      if (error.status === 402) {
        layoutService.setPaymentRequired();
        return false;
      }
    }

    const listRegistersResponse = await firstValueFrom(
      organisationApi.listRegisters(organisationId),
    );

    // Does the register even exist?
    const register = listRegistersResponse?.registers.find(r => r.id === registerId);
    if (register) {
      const organisationsResponse = await firstValueFrom(organisationApi.listOrganisations2());
      const organisation = organisationsResponse?.result.find(
        o => o.organisationId === organisationId,
      );

      if (!organisation) {
        return false;
      }

      if (await RegisterAccessGuard.isUserAuthorized(organisation, authorizationService)) {
        const modalRef = setupModal(ngbModal.open(RegisterAccessModalComponent), modal => {
          modal.register = register;
        });

        try {
          await modalRef.result;
          return true;
        } catch {
          // catch dismiss
        }
      }
    }

    await router.navigate(['/']);

    return false;
  }

  static async isUserAuthorized(
    organisation: OrganisationListItem,
    authorizationService: AuthorizationService,
  ) {
    return firstValueFrom(
      authorizationService
        .isOrganisationAuthorized(
          organisation.organisationId,
          OrganisationPermissionType.UpdateUser,
        )
        .pipe(firstWithContext()),
    );
  }
}
